import { useState, useEffect, useRef } from 'react'
import { Unsubscribe } from 'firebase/firestore'
import {
  subscribeToPublicProfile,
  getPublicTeacherProfile,
} from '../services/publicProfileService'
import { TeacherProfile } from '../services/teacherProfileService'

// ============================================================================
// PUBLIC TEACHER PROFILE HOOK
// ============================================================================
// Read-only hook that reads from publicTeacherProfiles/{teacherId}.
// Used for the shared Teacher Page link (/teachers/:teacherId).
// This collection has public read access — no authentication required.
// ============================================================================

interface UsePublicTeacherProfileReturn {
  profile: Partial<TeacherProfile> | null
  loading: boolean
  error: Error | null
}

export const usePublicTeacherProfile = (
  teacherId: string | undefined | null,
): UsePublicTeacherProfileReturn => {
  const [profile, setProfile] = useState<Partial<TeacherProfile> | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  const isMountedRef = useRef<boolean>(true)
  const unsubscribeRef = useRef<Unsubscribe | null>(null)

  useEffect(() => {
    isMountedRef.current = true

    if (!teacherId) {
      setProfile(null)
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    console.log(
      `[usePublicTeacherProfile] Setting up listener for ${teacherId}`,
    )

    // Subscribe to real-time updates on the public profile
    const unsubscribe = subscribeToPublicProfile(
      teacherId,
      (data) => {
        if (!isMountedRef.current) return

        if (data) {
          console.log(
            `[usePublicTeacherProfile] Profile loaded for ${teacherId}`,
          )
          setProfile(data as Partial<TeacherProfile>)
          setError(null)
        } else {
          console.warn(
            `[usePublicTeacherProfile] Public profile not found for ${teacherId}`,
          )
          setProfile(null)
          setError(
            new Error(
              'Teacher profile not found. The teacher may not have published their profile yet.',
            ),
          )
        }
        setLoading(false)
      },
      (err) => {
        if (!isMountedRef.current) return
        console.error('[usePublicTeacherProfile] Error:', err)
        setError(err)
        setLoading(false)
      },
    )

    unsubscribeRef.current = unsubscribe

    return () => {
      console.log(
        `[usePublicTeacherProfile] Cleaning up listener for ${teacherId}`,
      )
      isMountedRef.current = false
      unsubscribe()
      unsubscribeRef.current = null
    }
  }, [teacherId])

  // Reset mounted ref on each render cycle
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if (unsubscribeRef.current) {
        unsubscribeRef.current()
        unsubscribeRef.current = null
      }
    }
  }, [])

  return {
    profile,
    loading,
    error,
  }
}
